import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { apiCallStatus } from 'helpers/constants';

// Helper function to extract error code from different patterns
const extractErrorCode = (value, checkErrorResponse) => {
    // Pattern 1: Direct errorCode property
    if (value?.errorCode >= 400) {
        return { code: value.errorCode, message: value?.errorMessage };
    }
    // Pattern 2: Error in nested error.response.status
    if (checkErrorResponse && value?.error?.response?.status >= 400) {
        return {
            code: value.error.response.status,
            message: value?.error?.message || value?.error?.response?.statusText
        };
    }
    // Pattern 3: Error in nested error.status or error.statusCode
    if (checkErrorResponse && (value?.error?.status >= 400 || value?.error?.statusCode >= 400)) {
        return { code: value.error.status || value.error.statusCode, message: value?.error?.message };
    }
    // Pattern 4: Fallback - any ERROR status without specific error codes
    return {
        code: 500,
        message: value?.errorMessage || value?.message || 'Unknown API error'
    };
};

// Helper function to create and throw error
const throwHttpError = (errorCode, errorMessage, currentPath, originalError, enableDebugLogging) => {
    const fullErrorMessage = `HTTP ${errorCode} error in ${currentPath}: ${errorMessage}`;

    if (enableDebugLogging) {
        console.error('🚨 ErrorBoundary Trigger - Throwing error:', {
            errorCode,
            message: fullErrorMessage,
            path: currentPath,
            originalError
        });
    }

    const error = new Error(fullErrorMessage);
    error.response = { status: errorCode };
    error.originalError = originalError;
    throw error;
};

// Helper function to log debug information
const logDebugInfo = (value, currentPath) => {
    console.log('🔍 ErrorBoundary Trigger - Found ERROR state:', {
        path: currentPath,
        loadingStatus: value.loadingStatus,
        errorCode: value.errorCode,
        error: value.error,
        errorResponse: value.error?.response,
        fullValue: value
    });
    console.log('🔍 ErrorBoundary Trigger - All properties:', Object.keys(value));
};

// Recursive function to check for HTTP errors
const checkForHttpErrors = (obj, path, options) => {
    const { excludeErrorCodes, enableDebugLogging, checkErrorResponse } = options;

    if (!obj || typeof obj !== 'object') {
        return;
    }

    Object.keys(obj).forEach(key => {
        const value = obj[key];
        const currentPath = path ? `${path}.${key}` : key;

        if (!value || typeof value !== 'object') {
            return;
        }

        // Debug logging for ERROR states
        if (enableDebugLogging && value?.loadingStatus === apiCallStatus.ERROR) {
            logDebugInfo(value, currentPath);
        }

        // Check if this is an error state
        if (value?.loadingStatus === apiCallStatus.ERROR) {
            if (value?.suppressGlobalNotification) {
                return;
            }

            const { code: errorCode, message: errorMessage } = extractErrorCode(value, checkErrorResponse);

            // Trigger error if not excluded
            if (errorCode && !excludeErrorCodes.includes(errorCode)) {
                throwHttpError(errorCode, errorMessage || 'API call failed', currentPath, value?.error, enableDebugLogging);
            }
        }

        // Recursively check nested objects
        checkForHttpErrors(value, currentPath, options);
    });
};

// eslint-disable-next-line import/no-unused-modules
export const withErrorBoundaryTrigger = (WrappedComponent, options = {}) => {
    const {
        excludeErrorCodes = [401, 417],
        enableDebugLogging = process.env.NODE_ENV === 'development',
        checkErrorResponse = true
    } = options;

    const ErrorBoundaryTriggerWrapper = (props) => {
        const { model } = props;

        useEffect(() => {
            if (enableDebugLogging) {
                console.log('🔄 ErrorBoundary Trigger - Checking model:', model);
            }

            const checkOptions = { excludeErrorCodes, enableDebugLogging, checkErrorResponse };
            checkForHttpErrors(model, '', checkOptions);
        }, [model]);

        return <WrappedComponent {...props} />;
    };

    ErrorBoundaryTriggerWrapper.propTypes = {
        model: PropTypes.object
    };

    return ErrorBoundaryTriggerWrapper;
};
